// POS phase 3 §13.1 — BO access to the POS pages (twin of bo-api posBOAccess).
// roles.pos_bo present → that value; absent (legacy roles JSON) → pos >= 2;
// group 99 → always (level 7).
export function posBOAccess(user) {
	const u = user || {};
	if (Number(u.group) === 99) return { bo: true, level: 7 };
	let roles = u.roles || {};
	if (typeof roles === "string") {
		try {
			roles = JSON.parse(roles) || {};
		} catch (e) {
			roles = {};
		}
	}
	const level = Number(roles.pos) || 0;
	if (roles.pos_bo !== undefined && roles.pos_bo !== null) return { bo: !!roles.pos_bo, level };
	return { bo: level >= 2, level };
}

// The users-page checkbox for a stored roles object (legacy → pos >= 2).
export function posBOChecked(roles) {
	const r = roles || {};
	if (r.pos_bo !== undefined && r.pos_bo !== null) return !!r.pos_bo;
	return (Number(r.pos) || 0) >= 2;
}

// ---- users page (bo-fn #448 B1 / B2) ---------------------------------------
// Admin (1) and Owner (2) get POS level 4 from their group (bo-api RoleByGroup;
// MergeRole keeps the higher level at login), SuperAdmin (99) is always level 7.
// The STORED roles.pos is the user's OWN level: the form shows max(own, group)
// but never writes the group's level into the record — otherwise demoting an
// Admin back to employee would keep POS admin (B2). Their stored roles often
// have no "pos" key at all; an untouched save must not write {"pos":0,
// "pos_bo":false} and strip their BO access (B1).
export function posGroupFloor(group) {
	const g = Number(group);
	return g === 1 || g === 2 || g === 99 ? 4 : 0;
}

// Groups whose POS level comes from the group: the level select is locked at admin.
export function posGroupLocked(group) {
	return posGroupFloor(group) > 0;
}

// Nearest option of the level select (0 / 1 seller / 2 supervisor / 4 admin).
export function posLevelOption(n) {
	const v = Number(n) || 0;
	return v >= 4 ? 4 : v >= 2 ? 2 : v >= 1 ? 1 : 0;
}

export function posEffectiveLevel(roles, group) {
	const r = roles || {};
	return posLevelOption(Math.max(Number(r.pos) || 0, posGroupFloor(group)));
}

// pos_bo when the roles JSON has no explicit key (§13.1 legacy rule).
export function posBOLegacy(level, group) {
	return Number(group) === 99 || (Number(level) || 0) >= 2;
}

export function posBOExplicit(roles) {
	const r = roles || {};
	return r.pos_bo !== undefined && r.pos_bo !== null;
}

// Initial form values + state for a stored roles object and group.
// state: { stored, explicit, storedLevel, ownLevel, autoBO }
export function posRoleForm(roles, group) {
	const r = roles || {};
	const storedLevel = posLevelOption(r.pos);
	const pos = Math.max(storedLevel, posGroupFloor(group));
	const explicit = posBOExplicit(r);
	let bo = explicit ? !!r.pos_bo : posBOLegacy(pos, group);
	if (Number(group) === 99) bo = true;
	return { form: { pos, pos_bo: bo }, state: { stored: r, explicit, storedLevel, ownLevel: storedLevel, autoBO: bo } };
}

// The untouched box follows the legacy rule on the effective level; group 99 is always ticked.
function posBOFollow(form, state, group) {
	if (Number(group) === 99) {
		form.pos_bo = true;
	} else if (!state.explicit && form.pos_bo === state.autoBO) {
		state.autoBO = posBOLegacy(Math.max(state.ownLevel, posGroupFloor(group)), group);
		form.pos_bo = state.autoBO;
	}
}

// The level select changed. Unlocked groups: the select is the user's own level.
export function posLevelPicked(form, state, group) {
	if (!posGroupLocked(group)) state.ownLevel = posLevelOption(form.pos);
	posBOFollow(form, state, group);
	return { form, state };
}

// The group changed: show max(own level, group level) — demoting an Admin back
// to employee shows their own level again (B2).
export function posGroupChanged(form, state, group) {
	form.pos = posGroupLocked(group) ? 4 : state.ownLevel;
	posBOFollow(form, state, group);
	return { form, state };
}

// Keys of the users-page checkboxes (saved as 7 / 0).
export const USER_ROLE_CHECKBOX_KEYS = ["wms", "order", "order_overview", "packing", "packing_admin", "payment", "report", "product", "customer", "delivery", "order_return",
	"coupon", "coupon_member", "po", "platform_statement", "account", "dashboard_by_platform_channel", "agent_commission", "seller", "modern_trade", "shipping_config",
	// Phase 1 ads §6.1 — appended (marketing landing pages + source report).
	"marketing",
	// Phase 3 affiliate §5.1 — appended (documents / payout rounds).
	"affiliate_finance"];

// The roles JSON to save: MERGED onto the stored roles, so keys the form does not
// show (kol, kol_financial, reprocess, …) and untouched values (e.g. wms:1) are
// kept as they are. A key is written only when the form changed it.
//  - pos: the user's OWN level, written only when it changed — never the group's level (B2).
//  - pos_bo: kept when it was explicit; otherwise written only when the box differs
//    from the legacy rule on the effective level max(own, group) (B1). Group 99: untouched.
export function userRolesForSave(stored, form, state, group) {
	const r = Object.assign({}, stored || {});
	const f = form || {};
	USER_ROLE_CHECKBOX_KEYS.forEach((k) => {
		const was = (Number(r[k]) || 0) > 0;
		if (was !== !!f[k]) r[k] = f[k] ? 7 : 0;
	});
	if ((Number(r.member) || 0) !== (Number(f.member) || 0)) r.member = Number(f.member) || 0;
	if (state.ownLevel !== state.storedLevel) r.pos = state.ownLevel;
	if (Number(group) !== 99) {
		const bo = !!f.pos_bo;
		const legacy = posBOLegacy(Math.max(state.ownLevel, posGroupFloor(group)), group);
		if (state.explicit || bo !== legacy) r.pos_bo = bo;
		else delete r.pos_bo;
	}
	return r;
}

// Users-page checkbox / member values for stored roles ({} = a new user: nothing
// ticked; the group's defaults are added by bo-api at login, RoleByGroup).
export function userRoleFields(stored) {
	const r = stored || {};
	const out = { member: Number(r.member) || 0 };
	USER_ROLE_CHECKBOX_KEYS.forEach((k) => (out[k] = (Number(r[k]) || 0) > 0));
	return out;
}
